import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { ToastService, Toast } from './toast.service';
import { SafeHtmlPipe } from '../../pipe/safe-html.pipe';

@Component({
    selector: 'app-toast',
    standalone: true,
    imports: [CommonModule, SafeHtmlPipe],
    animations: [
        trigger('toastAnim', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateX(100%)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateX(100%)' })),
            ]),
        ]),
    ],
    template: `
        <div class="fixed top-5 right-5 z-[99999] flex flex-col gap-3 max-w-sm w-full pointer-events-none">
            @for (toast of toastService.toasts(); track toast.id) {
                <div @toastAnim
                     class="pointer-events-auto rounded-lg border px-4 py-3 shadow-lg flex items-start gap-3 backdrop-blur-sm"
                     [ngClass]="getToastClasses(toast)">
                    <!-- Icon -->
                    <span class="mt-0.5 flex-shrink-0" [innerHTML]="getIcon(toast.type) | safeHtml"></span>
                    <!-- Message -->
                    <span class="text-sm font-medium flex-1">{{ toast.message }}</span>
                    <!-- Dismiss -->
                    <button (click)="toastService.dismiss(toast.id)"
                            class="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity mt-0.5">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
            }
        </div>
    `,
})
export class ToastComponent {
    readonly toastService = inject(ToastService);

    getToastClasses(toast: Toast): Record<string, boolean> {
        return {
            'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300': toast.type === 'success',
            'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300': toast.type === 'error',
            'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300': toast.type === 'warning',
            'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300': toast.type === 'info',
        };
    }

    getIcon(type: string): string {
        switch (type) {
            case 'success':
                return '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>';
            case 'error':
                return '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>';
            case 'warning':
                return '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>';
            default:
                return '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>';
        }
    }
}
